/** biome-ignore-all lint/a11y/noStaticElementInteractions: for overlay */
/** biome-ignore-all lint/a11y/useKeyWithClickEvents: for overlay */
import { Flexbox, VerticalFlexbox } from "@src/components/layout/Flexbox";
import { useIsChromeExtension } from "@src/providers/isChromeExtension";
import { SidebarProvider } from "@src/providers/sidebarProvider";
import { useEffect, useState } from "react";
import { Header } from "./components/Header";
import { Sidebar } from "./components/Sidebar";

export interface DashboardLayoutProps {
  children: React.ReactNode;
  /**
   * Function to close the dashboard layout when embedded
   * in other contexts (Chrome extension)
   */
  onClose?: () => void;
}

const Content = ({ children }: React.PropsWithChildren) => {
  return (
    <VerticalFlexbox className="min-h-0 flex-1 overflow-y-auto bg-muted/30">
      {children}
    </VerticalFlexbox>
  );
};

const DESKTOP_MEDIA_QUERY = "(min-width: 768px)";

export const DashboardLayout = ({
  children,
  onClose,
}: DashboardLayoutProps) => {
  const isChromeExtension = useIsChromeExtension();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia(DESKTOP_MEDIA_QUERY);
    setIsSidebarOpen(media.matches);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Sidebar temporarily hidden until the encounter list returns and we rework
  // the navigation pattern. Keep the component wired up so we can re-enable
  // it by flipping this flag back to the original expression.
  const showSidebar = false;

  return (
    <Flexbox className="h-full min-h-screen w-full bg-background">
      {showSidebar ? (
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={closeSidebar}
          onToggle={toggleSidebar}
        />
      ) : null}
      {showSidebar && isSidebarOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-20 bg-black/40 md:hidden"
          aria-label="Close sidebar"
          onClick={closeSidebar}
        />
      ) : null}
      <VerticalFlexbox className="min-w-0 flex-1">
        <Header
          onMenuToggle={toggleSidebar}
          isChromeExtension={isChromeExtension}
          showMenuToggle={showSidebar}
          onClose={onClose}
        />
        <SidebarProvider>
          <VerticalFlexbox as="main" className="min-h-0 flex-1">
            <Content>{children}</Content>
          </VerticalFlexbox>
        </SidebarProvider>
      </VerticalFlexbox>
    </Flexbox>
  );
};
